import { BASE_API_URL } from '@api';
import {
    AiMessageTypes,
    Author,
    type AgentMessage,
    type AIMessageContext,
} from '@diagon-alley/common';
import useToaster from '@hooks/toaster/useToaster';
import { useCallback, useRef, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';

export interface StreamMessage {
    content: string;
    messageId?: string;
    traceId?: string;
}

interface UseAIStreamMutationOptions {
    projectUuid?: string;
    context: AIMessageContext;
    /** Callback fired when an error occurs */
    onError?: (error: Error) => void;
    /** Callback fired when messages are received */
    onMessagesReceived?: (messages: AgentMessage[]) => void;
    /** Callback fired when threadId is received from the stream */
    onThreadReceived?: (threadId: string, resourceId: string) => void;
    /** Callback fired when stream completes successfully */
    onSuccess?: (threadId: string, messages: AgentMessage[]) => void;
    /** Callback fired when a status/processing update is received */
    onStatusReceived?: (status: string) => void;
    /** Whether to show toast notifications for errors */
    showErrorToast?: boolean;
}

interface UseAIStreamMutationReturn {
    /** Whether there's an ongoing request */
    isLoading: boolean;
    /** Current error state */
    error: Error | null;
    /** Send a message to the AI stream */
    sendMessage: (message: StreamMessage, threadId?: string) => Promise<void>;
    /** Abort the current stream */
    abortStream: () => void;
    /** Clear the current error */
    clearError: () => void;
    /** The last sent message for resending */
    lastMessage: StreamMessage | null;
}

/**
 * Generic hook for handling AI streaming mutations with abort controller support
 * Adapted for diagon-alley's SSE endpoint
 */
export const useAIStreamMutation = (
    options: UseAIStreamMutationOptions,
): UseAIStreamMutationReturn => {
    const {
        projectUuid,
        context,
        onError,
        onMessagesReceived,
        onThreadReceived,
        onSuccess,
        onStatusReceived,
        showErrorToast = true,
    } = options;

    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<Error | null>(null);
    const [lastMessage, setLastMessage] = useState<StreamMessage | null>(null);

    // Track messages received during current stream for onSuccess callback
    const currentStreamMessagesRef = useRef<AgentMessage[]>([]);
    const currentThreadIdRef = useRef<string | null>(null);
    const currentAssistantMessageRef = useRef<AgentMessage | null>(null);
    const streamEndedRef = useRef<boolean>(false);
    const abortControllerRef = useRef<AbortController | null>(null);
    const { showToastError } = useToaster();

    /**
     * Clears the current error state
     */
    const clearError = useCallback(() => {
        setError(null);
    }, []);

    /**
     * Aborts the current streaming request
     */
    const abortStream = useCallback(() => {
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
            abortControllerRef.current = null;
            setIsLoading(false);
        }
    }, []);

    /**
     * Parses SSE stream response from the AI API
     * The backend sends SSE format: "data: <chunk>\n\n"
     */
    const parseSSEStream = useCallback(
        async (readableStream: ReadableStream) => {
            const appendTextDelta = (text: string) => {
                if (!text) return;

                const threadId = currentThreadIdRef.current || '';

                const baseMessage: AgentMessage =
                    currentAssistantMessageRef.current ?? {
                        threadId,
                        messageId: uuidv4(),
                        author: Author.SYSTEM,
                        context,
                        content: {
                            type: AiMessageTypes.TEXT,
                            value: '',
                        },
                        traceId: '',
                    };

                const previousValue =
                    baseMessage.content.type === AiMessageTypes.TEXT
                        ? baseMessage.content.value
                        : '';

                const updatedMessage: AgentMessage = {
                    ...baseMessage,
                    threadId,
                    content: {
                        type: AiMessageTypes.TEXT,
                        value: previousValue + text,
                    },
                };

                currentAssistantMessageRef.current = updatedMessage;

                const existingIndex =
                    currentStreamMessagesRef.current.findIndex(
                        (msg) => msg.messageId === updatedMessage.messageId,
                    );

                if (existingIndex >= 0) {
                    currentStreamMessagesRef.current[existingIndex] =
                        updatedMessage;
                } else {
                    currentStreamMessagesRef.current.push(updatedMessage);
                }

                onMessagesReceived?.([updatedMessage]);
            };

            const isAgentMessage = (
                payload: unknown,
            ): payload is AgentMessage =>
                !!payload &&
                typeof payload === 'object' &&
                'content' in payload &&
                (payload as AgentMessage).content !== undefined &&
                typeof (payload as AgentMessage).content === 'object' &&
                'type' in (payload as AgentMessage).content;

            const handleParsedChunk = (chunk: unknown) => {
                if (Array.isArray(chunk)) {
                    chunk.forEach(handleParsedChunk);
                    return;
                }

                if (
                    chunk &&
                    typeof chunk === 'object' &&
                    (chunk as { type?: string }).type === 'thread' &&
                    (chunk as { threadId?: string }).threadId
                ) {
                    const threadPayload = chunk as {
                        threadId: string;
                        resourceId?: string;
                    };
                    currentThreadIdRef.current = threadPayload.threadId;
                    if (
                        currentAssistantMessageRef.current &&
                        !currentAssistantMessageRef.current.threadId
                    ) {
                        currentAssistantMessageRef.current.threadId =
                            threadPayload.threadId;
                    }
                    onThreadReceived?.(
                        threadPayload.threadId,
                        threadPayload.resourceId || '',
                    );
                    return;
                }

                if (
                    chunk &&
                    typeof chunk === 'object' &&
                    (chunk as { type?: string }).type === 'status'
                ) {
                    const statusChunk = chunk as { content?: string };
                    if (statusChunk.content) {
                        onStatusReceived?.(statusChunk.content);
                    }
                    return;
                }

                if (
                    chunk &&
                    typeof chunk === 'object' &&
                    (chunk as { type?: string }).type === 'end'
                ) {
                    streamEndedRef.current = true;
                    return;
                }

                if (isAgentMessage(chunk)) {
                    const agentMessage = chunk as AgentMessage;
                    const existingIndex =
                        currentStreamMessagesRef.current.findIndex(
                            (msg) => msg.messageId === agentMessage.messageId,
                        );

                    if (existingIndex >= 0) {
                        currentStreamMessagesRef.current[existingIndex] =
                            agentMessage;
                    } else {
                        currentStreamMessagesRef.current.push(agentMessage);
                    }
                    onMessagesReceived?.([agentMessage]);
                    return;
                }

                if (
                    chunk &&
                    typeof chunk === 'object' &&
                    (chunk as { type?: string }).type === 'text-delta'
                ) {
                    const deltaChunk = chunk as {
                        content?: string;
                        payload?: { text?: string };
                    };
                    appendTextDelta(
                        deltaChunk.content || deltaChunk.payload?.text || '',
                    );
                    return;
                }

                if (typeof chunk === 'string') {
                    appendTextDelta(chunk);
                }
            };

            const reader = readableStream.getReader();
            const decoder = new TextDecoder();
            let done = false;
            let buffer = '';

            try {
                while (!done) {
                    const { value, done: doneReading } = await reader.read();
                    done = doneReading;

                    if (value) {
                        buffer += decoder.decode(value, { stream: true });

                        // Process SSE format: "data: <chunk>\n\n"
                        const lines = buffer.split('\n');
                        buffer = lines.pop() || ''; // Keep incomplete line in buffer

                        for (const line of lines) {
                            if (line.startsWith('data: ')) {
                                const data = line.slice(6); // Remove "data: " prefix

                                if (data === '[DONE]') {
                                    continue;
                                }

                                try {
                                    // Try to parse as JSON
                                    const parsed = JSON.parse(data);

                                    handleParsedChunk(parsed);
                                } catch (parseError) {
                                    // If not JSON, treat as plain text chunk
                                    // Create a text message from the chunk
                                    appendTextDelta(data);
                                }
                            }
                        }
                    }
                }

                // Process any remaining buffer
                if (buffer.trim()) {
                    if (buffer.startsWith('data: ')) {
                        const data = buffer.slice(6);
                        try {
                            const parsed = JSON.parse(data);
                            handleParsedChunk(parsed);
                        } catch {
                            // Ignore parse errors for final buffer
                        }
                    }
                }
            } catch (streamError) {
                if (
                    streamError instanceof Error &&
                    streamError.name !== 'AbortError'
                ) {
                    throw streamError;
                }
            } finally {
                reader.releaseLock();
                streamEndedRef.current = true;
            }
        },
        [context, onMessagesReceived, onThreadReceived],
    );

    /**
     * Sends a message to the AI stream
     */
    const sendMessage = useCallback(
        async (message: StreamMessage, threadId?: string) => {
            // Abort any existing stream
            abortStream();

            // Create new abort controller
            abortControllerRef.current = new AbortController();

            try {
                setIsLoading(true);
                setError(null);
                setLastMessage(message);

                // Reset messages and threadId for new stream
                currentStreamMessagesRef.current = [];
                currentAssistantMessageRef.current = null;
                currentThreadIdRef.current = threadId ?? null;
                streamEndedRef.current = false;

                // Prepare request body matching ChatRequestDto
                const requestBody: {
                    messages: Array<{ role: string; content: string }>;
                    threadId?: string;
                    resourceId?: string;
                } = {
                    messages: [
                        {
                            role: 'USER',
                            content: message.content,
                        },
                    ],
                };

                // Add threadId if provided
                if (threadId) {
                    requestBody.threadId = threadId;
                }

                // Build headers with auth token
                const headers: Record<string, string> = {
                    'Content-Type': 'application/json',
                };
                try {
                    const token = localStorage.getItem('token');
                    if (token) {
                        headers.Authorization = `Bearer ${token}`;
                    }
                } catch {
                    // Ignore localStorage errors
                }

                const response = await fetch(
                    `${BASE_API_URL}/api/v1/ai/stream`,
                    {
                        method: 'POST',
                        headers,
                        body: JSON.stringify(requestBody),
                        signal: abortControllerRef.current.signal,
                    },
                );

                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }

                if (response.body) {
                    await parseSSEStream(response.body);

                    // Use threadId from stream if available, otherwise use provided threadId
                    const finalThreadId =
                        currentThreadIdRef.current || threadId || '';

                    // Call onSuccess when stream completes successfully
                    if (finalThreadId) {
                        onSuccess?.(
                            finalThreadId,
                            currentStreamMessagesRef.current,
                        );
                    }
                }
            } catch (err) {
                const requestError =
                    err instanceof Error
                        ? err
                        : new Error('Unknown error occurred');

                // Don't set error for aborted requests
                if (requestError.name !== 'AbortError') {
                    setError(requestError);
                    onError?.(requestError);

                    if (showErrorToast) {
                        showToastError({
                            title: 'Failed to send message',
                        });
                    }
                }
            } finally {
                if (!streamEndedRef.current) {
                    streamEndedRef.current = true;
                }
                setIsLoading(false);
                abortControllerRef.current = null;
            }
        },
        [
            abortStream,
            parseSSEStream,
            onError,
            onSuccess,
            showErrorToast,
            showToastError,
        ],
    );

    return {
        isLoading,
        error,
        sendMessage,
        abortStream,
        clearError,
        lastMessage,
    };
};

export default useAIStreamMutation;
